import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

/** Parse API comma-separated `termsAndConditions` into editable lines */
export function parseTermsAndConditionsApiValue(
  raw: string | undefined | null
): string[] {
  if (raw == null || String(raw).trim() === "") return [];
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Serialize UI lines to API comma-separated string (empty points dropped) */
export function serializeTermsAndConditionsForApi(lines: string[]): string {
  return lines.map((s) => s.trim()).filter(Boolean).join(",");
}

type TermsAndConditionsSectionProps = {
  lines: string[];
  onChange: (next: string[]) => void;
};

export function TermsAndConditionsSection({
  lines,
  onChange,
}: TermsAndConditionsSectionProps) {
  const add = () => onChange([...lines, ""]);
  const remove = (index: number) =>
    onChange(lines.filter((_, i) => i !== index));
  const update = (index: number, value: string) =>
    onChange(lines.map((line, i) => (i === index ? value : line)));

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-semibold text-gray-800">
          Terms & conditions
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="gap-1 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>
      <div className="space-y-2">
        {lines.length === 0 ? (
          <p className="text-sm text-gray-500 py-1">
            No terms yet. Click Add to add a point.
          </p>
        ) : (
          lines.map((line, index) => (
            <div key={index} className="flex gap-2 items-start">
              <Input
                value={line}
                onChange={(e) => update(index, e.target.value)}
                placeholder={`Condition point ${index + 1}`}
                className="flex-1 bg-white"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => remove(index)}
                aria-label={`Remove condition ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
